import { useEffect, useState } from "react";
import { api, day } from "../../api.js";
import { shortName } from "../common.jsx";
import { loadKey, signHash } from "../../kep.js";
import CertField from "../../CertField.jsx";
import Icon from "../../icons.jsx";
import { SuggestInput } from "../pn/Suggest.jsx";
import "../requests/requests.css";
import "./trust.css";

// «Бізнес-партнерство» (server/src/modules/partner-trust.js): ініціатор пропонує партнерство й одразу підписує свою згоду
// в кабінеті ДПС (відомості про себе); партнер отримує «Вам запропонували бізнес-партнерство», приймає й підписує свою —
// і обидві сторони бачать відомості одна одної. Згода — ключем керівника, за замовчуванням усі види відомостей.

// Форма згоди: види відомостей (усі вибрано) і ключ керівника; onSubmit(prep, signature) — пропозиція чи прийняття
function ConsentForm({ company, tin, title, intro, submitLabel, onSubmit, onCancel, cancelLabel = "Скасувати" }) {
  const base = `/api/companies/${company.id}/trust`;
  const [prep, setPrep] = useState(null), [approval, setApproval] = useState([]);
  const [keyFile, setKeyFile] = useState(null), [certFiles, setCertFiles] = useState([]), [password, setPassword] = useState("");
  const [busy, setBusy] = useState(""), [err, setErr] = useState("");
  const allowed = ["submitter", "owner"].includes(company.role);
  useEffect(() => {
    if (!allowed) return;
    api(`${base}/consent/prepare`, { method: "POST", body: { tin } }).then((x) => { setPrep(x); setApproval(x.approval); }, (e) => setErr(e.message));
  }, [base, tin, allowed]);
  const toggle = (v) => setApproval((a) => (a.some((x) => String(x) === String(v)) ? a.filter((x) => String(x) !== String(v)) : [...a, v]));
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    try {
      setBusy("Відкриваємо ключ…");
      const { box, edrpou } = await loadKey({ keyFile, certFiles, password, log: setBusy });
      if (edrpou && edrpou !== company.edrpou) throw new Error("Ключ належить іншій компанії");
      setBusy("Готуємо згоду…");
      const p = await api(`${base}/consent/prepare`, { method: "POST", body: { tin, approval } });
      setBusy("Підписуємо…");
      const signature = await signHash(box, p.hash);
      setBusy("Передаємо згоду в кабінет ДПС…");
      await onSubmit(p, signature);
    } catch (x) { setErr(x.message); } finally { setBusy(""); setPassword(""); }
  };
  const all = prep?.types || [];
  return (
    <form className="card trust-accept" onSubmit={submit}>
      <div className="trust-accept-head">
        <span className="trust-avatar" aria-hidden="true"><Icon name="partners" /></span>
        <div><h3>{title}</h3>{intro && <p className="muted small">{intro}</p>}</div>
      </div>
      <div className="trust-accept-grid">
        <fieldset className="trust-types">
          <legend className="small"><b>Ваші відомості, які бачитиме партнер</b> · {approval.length} з {all.length || "…"}{all.length > 0 && <> · <button type="button" className="linkish" onClick={() => setApproval(approval.length === all.length ? [] : all.map((t) => t.value))}>{approval.length === all.length ? "зняти всі" : "вибрати всі"}</button></>}</legend>
          {!prep && allowed && !err && <p className="muted small" role="status">Читаємо перелік у кабінеті ДПС…</p>}
          <div className="trust-type-list">{all.map((t) => <label key={t.value} className="trust-type"><input type="checkbox" checked={approval.some((x) => String(x) === String(t.value))} onChange={() => toggle(t.value)} disabled={!!busy} /><span>{t.name}</span></label>)}</div>
        </fieldset>
        <div className="trust-key">
          <label className="field">Ключ керівника<input type="file" accept=".dat,.jks,.pfx,.zs2,.sk" required disabled={!!busy || !allowed} onChange={(e) => setKeyFile(e.target.files[0])} /></label>
          <label className="field">Пароль ключа<input type="password" autoComplete="off" value={password} required disabled={!!busy || !allowed} onChange={(e) => setPassword(e.target.value)} /></label>
          <CertField onChange={setCertFiles} disabled={!!busy || !allowed} />
          <p className="muted small">Ключ і пароль залишаються у браузері. Згоду ДПС приймає лише від керівника; припинити її можна будь-коли в кабінеті ДПС.</p>
          {err && <p className="error small" role="alert">{err}</p>}
          {busy && <p className="small" role="status">{busy}</p>}
          <div className="trust-buttons">
            <button type="submit" className="btn" disabled={!allowed || !keyFile || !password || !approval.length || !!busy}>{submitLabel}</button>
            {onCancel && <button type="button" className="btn btn-ghost" disabled={!!busy} onClick={onCancel}>{cancelLabel}</button>}
          </div>
          {!allowed && <p className="muted small">Підписати згоду може власник або користувач із роллю «подання».</p>}
        </div>
      </div>
    </form>
  );
}

// Запропонувати партнерство (з підписом своєї згоди) → пропозиція в eCabinet партнера; посилання — якщо партнера там ще немає
function ProposeTrust({ company, tin, name, onDone, onCancel }) {
  const [done, setDone] = useState(null), [copied, setCopied] = useState(false);
  const who = shortName(name) || `компанія з кодом ${tin}`;
  if (done) return (
    <div className="card trust-done" role="status">
      <h3>✓ Пропозицію надіслано</h3>
      <p className="small">{who} побачить у eCabinet «Вам запропонували бізнес-партнерство». Щойно приймуть і підпишуть свою згоду, ви обидва бачитимете відомості одне одного.</p>
      <p className="small muted">Якщо {who} ще не користується eCabinet — надішліть посилання: <button type="button" className="linkish" onClick={() => navigator.clipboard?.writeText(done.link).then(() => setCopied(true))}>{copied ? "скопійовано ✓" : "скопіювати посилання"}</button></p>
    </div>
  );
  return <ConsentForm company={company} tin={tin} title={`Запропонувати бізнес-партнерство: ${who}`}
    intro={`Код ${tin}. Ви одразу даєте згоду в кабінеті ДПС — партнер, прийнявши пропозицію й підписавши свою, бачитиме ваші відомості, а ви — його.`}
    submitLabel="Підписати й надіслати пропозицію" onCancel={onCancel}
    onSubmit={async (p, signature) => { const r = await api(`/api/companies/${company.id}/trust/propose`, { method: "POST", body: { tin, approval: p.approval, hash: p.hash, signature } }); setDone(r); onDone?.(r); }} />;
}

// Прийняти пропозицію: своя згода підписом ключа — обидві сторони бачать одна одну
export function AcceptTrust({ company, invite, onDone }) {
  const [done, setDone] = useState(false);
  const who = shortName(invite.from.name);
  if (done) return (
    <div className="card trust-done" role="status">
      <h3>✓ Ви — бізнес-партнери з {who}</h3>
      <p className="small">Тепер ви взаємно бачите офіційні відомості ДПС одне про одного — у досьє контрагента («Контрагенти» → {who}).{invite.fromConsented ? "" : ` Ми надіслали ${who} зустрічну пропозицію — їхні відомості з'являться, щойно приймуть.`}</p>
    </div>
  );
  return <ConsentForm company={company} tin={invite.from.tin} title={`Вам запропонували бізнес-партнерство: ${who}`}
    intro={`Код ${invite.from.tin}.${invite.fromConsented ? ` ${who} уже дав згоду — після вашого підпису ви бачитимете відомості одне одного.` : ""}`}
    submitLabel="Прийняти й підписати" cancelLabel="Відхилити"
    onCancel={async () => { await api(`/api/companies/${company.id}/trust/invite/${invite.id}/decline`, { method: "POST" }).catch(() => {}); onDone?.({ declined: true }); }}
    onSubmit={async (p, signature) => { const r = await api(`/api/companies/${company.id}/trust/consent`, { method: "POST", body: { tin: invite.from.tin, approval: p.approval, hash: p.hash, signature, inviteId: invite.id } }); setDone(true); onDone?.(r); }} />;
}

// Відомості ДПС про бізнес-партнера. Назви полів у відповіді кабінету не всі відомі, тож значення розпізнаємо за виглядом:
// код платежу — 8 цифр, рахунок — UA…, сума — число (поле st чи останнє), назва — найдовший текст; дати — дд.мм.рррр
const num = (v) => (v != null && /^-?\d+([.,]\d+)?$/.test(String(v).trim()) ? Number(String(v).replace(",", ".")) : null);
const uah = (n) => `${Number(n).toLocaleString("uk-UA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} грн`;
const dmy = (v) => { const m = String(v || "").match(/^(\d{4})-(\d{2})-(\d{2})/); return m ? `${m[3]}.${m[2]}.${m[1]}` : String(v || ""); };
const textOf = (o) => Object.values(o).filter((v) => /[A-Za-zА-Яа-яІіЇїЄєҐґ]/.test(v) && !/^UA\d/.test(v)).sort((x, y) => y.length - x.length)[0] || "";
function payment(o) {
  const vals = Object.values(o);
  const code = o.shot || vals.find((v) => /^\d{8}$/.test(v)) || "";
  const sum = num(o.st) ?? num(o.NDSSM) ?? num(o.ZN) ?? [...vals].reverse().map(num).find((x) => x != null && String(x) !== code) ?? null;
  return { code, name: o.shotName || textOf(o), sum };
}
const RIGHTS = [[/оренд|найм/i, "оренда"], [/власн/i, "власність"], [/користув/i, "користування"]];
const right = (v) => RIGHTS.find(([re]) => re.test(v || ""))?.[1] || v || "";
// Експорт/імпорт у кабінеті — лише { OPER: «експортних»/«імпортних», YES: «так»/«ні» }, без періоду й сум
const yesNo = (o) => { const what = String(o.OPER || Object.values(o).find((x) => !/^(так|ні)$/i.test(x)) || ""), yn = String(o.YES || Object.values(o).find((x) => /^(так|ні)$/i.test(x)) || "?").toLowerCase();
  return { what: /експорт/i.test(what) ? "Експорт" : /імпорт/i.test(what) ? "Імпорт" : what, yes: yn === "так", text: yn }; };
// «Так» — зеленим; «ні» — з поміткою: ДПС оновлює відомості із запізненням, за минулий звітний період
function ExportImport({ items }) {
  const list = items.map(yesNo);
  return (
    <span className="trust-ei">
      {list.map((x) => <span key={x.what} className={x.yes ? "ok-text" : "muted"}>{x.what}: <b>{x.text}</b></span>)}
      {list.some((x) => !x.yes) && <small className="muted">ДПС оновлює із запізненням — за минулий звітний період</small>}
    </span>
  );
}

// Плитка показника: «немає» — добре; є записи — увага
function Tile({ label, sec, value, neutral, hint }) {
  const none = sec?.state === "none", empty = sec?.state === "empty";
  return (
    <div className={`trust-tile ${none ? "is-na" : empty ? "is-ok" : neutral ? "is-info" : "is-warn"}`} title={hint}>
      <span className="trust-tile-l">{label}</span>
      <b>{none ? "без згоди" : empty ? "немає" : value}</b>
    </div>
  );
}

function PaymentsTable({ sec }) {
  const rows = sec.items.map(payment), total = rows.reduce((a, r) => a + (r.sum || 0), 0);
  return (
    <div className="trust-sec">
      <p className="trust-cell-h">{sec.label}</p>
      <table className="table-static trust-table"><tbody>
        {rows.map((r, i) => <tr key={i}><td className="wrap-cell" title={r.name}>{r.name || "—"}{r.code ? <span className="muted"> · {r.code}</span> : null}</td><td className="num">{r.sum == null ? "—" : uah(r.sum)}</td></tr>)}
      </tbody>{rows.length > 1 && <tfoot><tr><th scope="row">Разом</th><td className="num"><b>{uah(total)}</b></td></tr></tfoot>}</table>
    </div>
  );
}

function ObjectsTable({ sec }) {
  return (
    <div className="trust-sec">
      <p className="trust-cell-h">{sec.label}</p>
      <table className="table-static trust-table"><tbody>
        {sec.items.map((o, i) => <tr key={i}><td className="wrap-cell"><b>{o.NAME || o.TYPE_OBJECT_NAME || textOf(o)}</b>{o.TYPE_OBJECT_NAME && o.NAME && o.TYPE_OBJECT_NAME.toLowerCase() !== o.NAME.toLowerCase() ? <span className="muted"> · {o.TYPE_OBJECT_NAME.toLowerCase()}</span> : null}</td>
          <td>{right(o.TYPE_OF_RIGHTS)}</td><td className="num">{o.D_ACC_START ? `з ${dmy(o.D_ACC_START)}` : ""}{o.D_ACC_END ? ` по ${dmy(o.D_ACC_END)}` : ""}</td></tr>)}
      </tbody></table>
    </div>
  );
}

// Фінрезультат, залишкова вартість: «2025: 1 250 000,00 грн · подано 20.02.2026»
function FinSec({ sec }) {
  return (
    <div className="trust-sec">
      <p className="trust-cell-h">{sec.label}</p>
      <ul className="small trust-plain">{sec.items.map((o, i) => <li key={i}><b>{o.PERIOD || o.YYYY || ""}</b>{num(o.ZN) != null ? `: ${uah(num(o.ZN))}` : ""}{o.D_GET ? <span className="muted"> · подано {dmy(o.D_GET)}</span> : null}</li>)}</ul>
    </div>
  );
}

function GenericSec({ sec }) {
  return (
    <div className="trust-sec">
      <p className="trust-cell-h">{sec.label}</p>
      <ul className="small trust-plain">{sec.items.slice(0, 8).map((o, i) => <li key={i}>{Object.values(o).map((v) => (/^\d{4}-\d{2}-\d{2}/.test(v) ? dmy(v) : v)).join(" · ")}</li>)}</ul>
      {sec.items.length > 8 && <p className="muted small">ще {sec.items.length - 8}</p>}
    </div>
  );
}

function PartnerInfo({ st, self = false }) {
  const by = Object.fromEntries(st.sections.map((x) => [x.key, x]));
  const risk = by.risk, riskNow = risk?.items?.some((o) => !o.D_END);
  const pn = by.pnrk?.items?.map(payment) || [], late = by.late_pnrk?.items?.map(payment) || [];
  const cnt = (list) => list.reduce((a, o) => a + (num(o.CNT) || 1), 0);
  const sumOf = (sec) => (sec?.items || []).map(payment).reduce((a, r) => a + (r.sum || 0), 0);
  const tiles = [
    ["Ризиковість СМКОР", risk, riskNow ? "у переліку" : "була в переліку"],
    ["Зупинені ПН/РК (місяць)", by.pnrk, `${cnt(by.pnrk?.items || [])} на ${uah(pn.reduce((a, r) => a + (r.sum || 0), 0))}`],
    ["Несвоєчасні ПН/РК", by.late_pnrk, `${cnt(by.late_pnrk?.items || [])} на ${uah(late.reduce((a, r) => a + (r.sum || 0), 0))}`],
    ["Податковий борг", by.debt, uah(sumOf(by.debt))],
    ["Нараховано зобов'язань", by.accrued, uah(sumOf(by.accrued)), true],
    // Як відповідає ДПС («так»/«ні», період не вказано) — не перетворюємо на «немає»
    ["Експорт / імпорт · ДПС", by.export_import?.state === "empty" ? { state: "rows" } : by.export_import,
      by.export_import?.state === "empty" ? "інформація відсутня" : <ExportImport items={by.export_import?.items || []} />, true,
      "Відповідь кабінету ДПС «Наявність експортних/імпортних операцій» — так чи ні, без періоду й сум. За який період ДПС це визначає, кабінет не показує."],
  ];
  const shown = new Set(["risk", "pnrk", "late_pnrk", "debt", "accrued", "export_import"]);
  const detail = st.sections.filter((x) => !shown.has(x.key) && x.state === "rows");
  const empty = st.sections.filter((x) => !shown.has(x.key) && x.state === "empty").map((x) => x.label[0].toLowerCase() + x.label.slice(1));
  const none = st.sections.filter((x) => x.state === "none").map((x) => x.label[0].toLowerCase() + x.label.slice(1));
  return (
    <>
      <p className="trust-status">
        {!self && <span className="badge badge-ok">✓ Бізнес-партнер</span>}
        {risk?.state === "empty" && <span className="badge badge-ok">Не в переліку ризикових</span>}
        {risk?.state === "rows" && <span className={`badge ${riskNow ? "badge-bad" : "badge-warn"}`}>{riskNow ? "Ризиковий платник" : "Був у переліку ризикових"}</span>}
        <span className="muted small">дані ДПС на {new Date(st.checkedAt).toLocaleDateString("uk-UA")}</span>
      </p>
      <div className="trust-tiles">{tiles.map(([l, sec, v, neutral, hint]) => <Tile key={l} label={l} sec={sec} value={v} neutral={neutral} hint={hint} />)}</div>
      {risk?.state === "rows" && <ul className="small trust-risk">{risk.items.map((o, i) => <li key={i}>{o.D_BEGIN ? `Включено ${dmy(o.D_BEGIN)}` : ""}{o.D_END ? `, виключено ${dmy(o.D_END)}` : ""}{o.OPER ? ` · ${o.OPER}` : ""}</li>)}</ul>}
      {detail.length > 0 && <div className="trust-secs">{detail.map((x) => (x.key === "paid" ? <PaymentsTable key={x.key} sec={x} /> : x.key === "objects" ? <ObjectsTable key={x.key} sec={x} /> : x.key === "financial" || x.key === "assets" ? <FinSec key={x.key} sec={x} /> : <GenericSec key={x.key} sec={x} />))}</div>}
      {empty.length > 0 && <p className="muted small">Записів немає: {empty.join(", ")}.</p>}
      {none.length > 0 && <p className="muted small">{self ? "ДПС не показує" : "Без згоди партнера"}: {none.join(", ")}.</p>}
    </>
  );
}

// Блок у досьє контрагента: відомості партнера або «Запропонувати бізнес-партнерство» (з підписом своєї згоди)
export function TrustBlock({ companyId, company, tin, name, onInfo }) {
  const [st, setSt] = useState(null), [err, setErr] = useState(""), [form, setForm] = useState(false), [v, setV] = useState(0), [offer, setOffer] = useState(null);
  useEffect(() => {
    setSt(null);
    // Відомості з'явились (сервер зберіг знімок) — досьє перерахує СМКОР і оцінку
    api(`/api/companies/${companyId}/trust/partner/${tin}`).then((x) => { setSt(x); if (x.consent) onInfo?.(); }, (e) => setErr(e.message));
    api(`/api/companies/${companyId}/trust`).then((x) => setOffer(x.incoming.find((i) => i.from.tin === tin) || null), () => {});
  }, [companyId, tin, v]);
  const co = { ...company, id: companyId };
  return (
    <section className="card trust" aria-labelledby="tx-smkor">
      <h3 id="tx-smkor" className="block-title">Бізнес-партнерство</h3>
      {err && <p className="error small" role="alert">{err}</p>}
      {!st && !err && <p className="muted small" role="status">Перевіряємо в кабінеті ДПС…</p>}
      {st?.consent && <PartnerInfo st={st} />}
      {offer ? <AcceptTrust company={co} invite={offer} onDone={() => setV((x) => x + 1)} />
        : st && !st.consent && (form ? <ProposeTrust company={co} tin={tin} name={name} onCancel={() => setForm(false)} onDone={() => {}} /> : <>
          <p><span className="badge badge-idle">Ще не бізнес-партнер</span>{st.invite?.state === "sent" && <span className="badge badge-info trust-sent">пропозицію надіслано {day(st.invite.created_at)}</span>}
            {st.invite?.state === "accepted" && <span className="badge badge-warn trust-sent">партнер прийняв {day(st.invite.answered_at)}, але ДПС ще не показує відомості{st.reason ? `: ${st.reason}` : ""}</span>}</p>
          <p className="small">Офіційні відомості ДПС про контрагента — ризиковість СМКОР, зупинені ПН, борг, сплачені податки — доступні лише за його згодою. Запропонуйте бізнес-партнерство: ви підписуєте свою згоду, партнер приймає й підписує свою — і ви взаємно бачите відомості одне одного.</p>
          {company.role !== "viewer" && <button type="button" className="btn" onClick={() => setForm(true)}>Запропонувати бізнес-партнерство</button>}
        </>)}
    </section>
  );
}

// Головна: «Вам запропонували бізнес-партнерство» — лише коли є вхідні пропозиції
export function TrustOffers({ companyId, company, onChange }) {
  const [d, setD] = useState(null), [open, setOpen] = useState(null), [v, setV] = useState(0);
  useEffect(() => { api(`/api/companies/${companyId}/trust`).then(setD, () => setD(null)); }, [companyId, v]);
  if (!d?.incoming?.length) return null;
  return (
    <section className="card trust-inbox" aria-label="Вам запропонували бізнес-партнерство">
      {d.incoming.map((i) => open === i.id
        ? <AcceptTrust key={i.id} company={{ ...company, id: companyId }} invite={i} onDone={() => { setOpen(null); setV((x) => x + 1); onChange?.(); }} />
        : <div className="trust-request" key={i.id}>
          <span className="trust-avatar" aria-hidden="true"><Icon name="partners" /></span>
          <span><b>Вам запропонували бізнес-партнерство</b> — {shortName(i.from.name)}<br /><span className="muted small">код {i.from.tin} · {day(i.createdAt)}{i.fromConsented ? " · уже дали згоду на свої відомості" : ""}</span></span>
          <button type="button" className="btn btn-sm" onClick={() => setOpen(i.id)}>Переглянути</button>
        </div>)}
    </section>
  );
}

// Запропонувати партнерство будь-якому контрагенту: пошук (ваші ПН, контрагенти, ЄДР) → форма згоди з підписом
function ProposeAny({ companyId, company, onDone }) {
  const [q, setQ] = useState(""), [pick, setPick] = useState(null), [go, setGo] = useState(null);
  const tin = pick?.tin || (/^(\d{8}|\d{10})$/.test(q.trim()) ? q.trim() : "");
  if (company.role === "viewer") return null;
  if (go) return <ProposeTrust company={{ ...company, id: companyId }} tin={go.tin} name={go.name} onCancel={() => setGo(null)} onDone={onDone} />;
  return (
    <form className="card trust-propose" onSubmit={(e) => { e.preventDefault(); if (tin) setGo({ tin, name: pick?.name || null }); }}>
      <label className="field">Запропонувати бізнес-партнерство
        <SuggestInput id="trust-q" value={q} label="Контрагент: назва або код" minLength={2} placeholder="Назва, ЄДРПОУ або РНОКПП контрагента"
          onChange={(x) => { setQ(x); setPick(null); }} load={(x) => api(`/api/companies/${companyId}/pn/buyers?q=${encodeURIComponent(x)}`).then((r) => r.buyers)}
          onPick={(x) => { setPick(x); setQ(shortName(x.name) || x.tin); }}
          render={(x) => (<><span>{shortName(x.name) || x.tin}</span><span className="muted small">код {x.tin}{x.from === "edr" ? " · ЄДР" : ""}</span></>)} />
      </label>
      <button type="submit" className="btn" disabled={!tin}>Далі →</button>
    </form>
  );
}

// «Що бачать про вас партнери»: «Інформація про себе» з кабінету ДПС і розбіжності з даними eCabinet
function SelfInfo({ companyId }) {
  const [d, setD] = useState(null), [err, setErr] = useState("");
  useEffect(() => { api(`/api/companies/${companyId}/trust/self`).then(setD, (e) => setErr(e.message)); }, [companyId]);
  if (err) return null;
  const by = Object.fromEntries((d?.info?.sections || []).map((x) => [x.key, x]));
  const o = d?.ours || {};
  const ei = (by.export_import?.items || []).map(yesNo), dpsImp = ei.find((x) => x.what === "Імпорт"), dpsExp = ei.find((x) => x.what === "Експорт");
  const diff = [];
  if (dpsImp && !dpsImp.yes && (o.importMd || o.importSea)) diff.push(`Імпорт: ДПС — «ні», у eCabinet — МД ${o.importMd || o.importSea}${o.importLast ? `, остання ${dmy(String(o.importLast).slice(0, 10))}` : ""}`);
  if (dpsExp && !dpsExp.yes && o.exportMd) diff.push(`Експорт: ДПС — «ні», у eCabinet — МД ${o.exportMd}`);
  if (by.debt?.state === "empty" && o.debt > 0) diff.push(`Податковий борг: ДПС — немає, у «Розрахунках» — ${uah(o.debt)}`);
  if (by.pnrk?.state === "empty" && o.stopped > 0) diff.push(`Зупинені ПН: ДПС — немає за останній місяць, у СМКОР незакритих (зокрема давніших) — ${o.stopped}`);
  return (
    <details className="card trust-self" open={diff.length > 0}>
      <summary><b>Що бачать про вас партнери</b> <span className="muted small">— «Інформація про себе» з кабінету ДПС{diff.length ? ` · розбіжностей з eCabinet: ${diff.length}` : ""}</span></summary>
      {!d ? <p className="muted small" role="status">Читаємо кабінет ДПС…</p> : !d.info ? <p className="muted small">Кабінет ДПС не віддав відомості{d.reason ? `: ${d.reason}` : ""}.</p> : <>
        {diff.length > 0 && <div className="notice-warn small"><b>Розбіжності з даними eCabinet</b> — ДПС оновлює відомості із запізненням, за минулий звітний період:<ul>{diff.map((x) => <li key={x}>{x}</li>)}</ul></div>}
        <PartnerInfo st={{ ...d.info, checkedAt: d.checkedAt }} self />
      </>}
    </details>
  );
}

// Вкладка «Контрагенти → Бізнес-партнерство»: що це, вхідні пропозиції, запропонувати, перелік партнерів з обома
// напрямками згоди
const seen = (at, wait) => (at ? <span className="ok-text">✓ з {day(at)}</span> : wait ? <span className="muted">чекаємо відповіді з {day(wait)}</span> : <span className="muted">—</span>);
export function PartnershipTab({ companyId, company }) {
  const [d, setD] = useState(null), [v, setV] = useState(0);
  useEffect(() => { api(`/api/companies/${companyId}/trust`).then(setD, () => setD({ partners: [] })); }, [companyId, v]);
  const list = d?.partners || [];
  // Припинити свою згоду партнерові (кабінет ДПС, approval/cancel) — він перестане бачити ваші відомості
  const revoke = async (x) => {
    if (!confirm(`Припинити згоду для ${shortName(x.name) || x.tin}? Партнер перестане бачити ваші відомості ДПС.`)) return;
    try { await api(`/api/companies/${companyId}/trust/consents/cancel`, { method: "POST", body: { tin: x.tin } }); setV((n) => n + 1); }
    catch (e) { alert(e.message); }
  };
  return (
    <>
      <div className="card trust-about">
        <p className="small"><b>Бізнес-партнерство</b> — взаємна згода в кабінеті ДПС («Згода на передачу інформації»): ви й партнер бачите офіційні відомості одне про одного — чи є компанія в переліку ризикових за СМКОР (дати, критерій), зупинені ПН, податковий борг, сплачені податки, об'єкти, ліцензії, товари. Це захищає від угод з ризиковими контрагентами (коди 12 і 13 СМКОР). Пропозицію партнер приймає одним підписом ключа керівника; згоду можна припинити будь-коли в кабінеті ДПС.</p>
      </div>
      <TrustOffers companyId={companyId} company={company} onChange={() => setV((x) => x + 1)} />
      <SelfInfo companyId={companyId} />
      <ProposeAny companyId={companyId} company={company} onDone={() => setV((x) => x + 1)} />
      <section className="card" aria-labelledby="partners-h">
        <h3 id="partners-h" className="block-title">Ваші бізнес-партнери{list.length ? <span className="count">{list.length}</span> : null}</h3>
        {!d ? <p className="muted small" role="status">Завантаження…</p> : !list.length ? <p className="empty">Партнерів ще немає. Запропонуйте партнерство контрагенту вище або з його досьє.</p> : (
          <div className="table-wrap" tabIndex={0} role="region" aria-label="Бізнес-партнери">
            <table className="table-static">
              <thead><tr><th>Компанія</th><th>Ви бачите їхні відомості</th><th>Вони бачать ваші</th></tr></thead>
              <tbody>{list.map((x) => (
                <tr key={x.tin}>
                  <td className="wrap-cell"><a href={`#/c/${companyId}/partners/${x.tin}`}>{shortName(x.name) || `Код ${x.tin}`}</a><br /><span className="muted small">код {x.tin}</span></td>
                  <td>{seen(x.weSee, x.offered)}</td>
                  <td>{seen(x.theySee, null)}{x.theySee && ["submitter", "owner"].includes(company.role) && <> <button type="button" className="linkish small" onClick={() => revoke(x)}>припинити</button></>}</td>
                </tr>))}</tbody>
            </table>
          </div>)}
      </section>
    </>
  );
}

// Сторінка посилання #/invite/<токен>: знаходимо запит і компанію користувача з тим самим кодом
export function InvitePage({ token }) {
  const [d, setD] = useState(null), [err, setErr] = useState(""), [pick, setPick] = useState(null);
  useEffect(() => { api(`/api/trust/invite/${encodeURIComponent(token)}`).then((x) => { setD(x); if (x.companies.length === 1) setPick(x.companies[0]); }, (e) => setErr(e.message)); }, [token]);
  if (err) return <section className="card"><h2>Пропозиція бізнес-партнерства</h2><p className="error" role="alert">{err}</p></section>;
  if (!d) return <p className="muted" role="status">Відкриваємо запит…</p>;
  const inv = d.invite;
  if (inv.state !== "sent") return <section className="card"><h2>Пропозиція бізнес-партнерства</h2><p>Цю пропозицію вже {inv.state === "accepted" ? "прийнято" : inv.state === "declined" ? "відхилено" : "скасовано"}.</p><a href="#/">До компаній →</a></section>;
  return (
    <section className="trust-page">
      <h2>Пропозиція бізнес-партнерства</h2>
      {!d.companies.length ? (
        <div className="card">
          <p>{shortName(inv.from.name)} надіслав запит компанії з кодом <b>{inv.toTin}</b>. Щоб прийняти, додайте цю компанію в eCabinet входом ключем КЕП.</p>
          <a className="btn" href="#/add">Увійти ключем компанії {inv.toTin}</a>
          <p className="muted small">Після входу відкрийте це посилання ще раз.</p>
        </div>
      ) : !pick ? (
        <div className="card"><p>Виберіть компанію:</p>{d.companies.map((c) => <button key={c.id} type="button" className="btn btn-secondary" onClick={() => setPick(c)}>{shortName(c.name)}</button>)}</div>
      ) : <AcceptTrust company={pick} invite={inv} onDone={(r) => { if (r?.declined) location.hash = `#/c/${pick.id}`; }} />}
    </section>
  );
}
